import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './SpecialResponseRenderer.css';

const START_END_MARKERS = {
  navigation: ['::NAVIGATION_CARDS::', '::END_NAVIGATION_CARDS::'],
  product: ['::PRODUCT::', '::END_PRODUCT::'],
  productCards: ['::PRODUCT_CARDS::', '::END_PRODUCT_CARDS::'],
  trainingCards: ['::TRAINING_CARDS::', '::END_TRAINING_CARDS::'],
  productList: ['::PRODUCT_LIST::', '::END_PRODUCT_LIST::'],
  quiz: ['::QUIZ::', '::END_QUIZ::']
};

const containsMarkers = (text = '') =>
  Object.values(START_END_MARKERS).some(([start]) => text.includes(start));

const parseBlock = (text, startMarker, endMarker) => {
  if (!text || !text.includes(startMarker) || !text.includes(endMarker)) return null;

  const [before, rest] = text.split(startMarker);
  const [rawJson, after] = rest.split(endMarker);

  return {
    before: before?.trim() || '',
    raw: rawJson?.trim() || '',
    after: after?.trim() || ''
  };
};

const renderMarkdown = (content, key) => {
  if (!content) return null;
  return (
    <div className="assistant-special-content" key={key}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
  );
};

const normalizeProduct = (item) => {
  if (!item || typeof item !== 'object') return null;

  const title = item.title || item.name || '';
  if (!title) return null;

  const description = item.description || item.specs || '';
  const price = item.price ?? item.offers?.price ?? item.offers?.[0]?.price;
  const currency = item.currency || item.offers?.currency || item.offers?.[0]?.priceCurrency || '';
  const image = item.thumbnail || item.image || item.imageUrl || item.image?.[0] || null;
  const url = item.review_link || item.url || item.link || item.offers?.url || item.offers?.[0]?.url || null;

  return {
    title,
    description,
    price: price != null ? `${price}${currency ? ` ${currency}` : ''}` : null,
    image,
    url: typeof url === 'string' && /^https?:\/\//i.test(url) ? url : null
  };
};

const DEFAULT_PRIMARY = '#63AD61';
const DEFAULT_PRIMARY_LIGHT = '#E4ECE4';

const hexToRgb = (hex) => {
  const normalized = String(hex || '').trim().replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return { r: 99, g: 173, b: 97 };
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16)
  };
};

const getPrimaryTheme = () => {
  if (typeof window === 'undefined' || !window.getComputedStyle) {
    const rgb = hexToRgb(DEFAULT_PRIMARY);
    return {
      primaryColor: DEFAULT_PRIMARY,
      primaryLight: DEFAULT_PRIMARY_LIGHT,
      navGradient: `linear-gradient(135deg, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8) 0%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.6) 100%)`,
      imagePlaceholderGradient: `linear-gradient(135deg, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25) 0%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12) 100%)`
    };
  }

  const rootStyles = window.getComputedStyle(document.documentElement);
  const primaryColor = rootStyles.getPropertyValue('--primary-color').trim() || DEFAULT_PRIMARY;
  const primaryLight = rootStyles.getPropertyValue('--primary-light').trim() || DEFAULT_PRIMARY_LIGHT;
  const rgb = hexToRgb(primaryColor);

  return {
    primaryColor,
    primaryLight,
    navGradient: `linear-gradient(135deg, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8) 0%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.6) 100%)`,
    imagePlaceholderGradient: `linear-gradient(135deg, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25) 0%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12) 100%)`
  };
};

const PlaceholderIcon = ({ className = '', style }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={style}
    aria-hidden="true"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="9" cy="9" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const NavigationCards = ({ content }) => {
  const block = React.useMemo(
    () => parseBlock(content, ...START_END_MARKERS.navigation),
    [content]
  );

  const cards = React.useMemo(() => {
    if (!block) return null;
    try {
      const parsed = JSON.parse(block.raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }, [block]);

  const cardsRef = React.useRef(null);
  const [isScrolledToEnd, setIsScrolledToEnd] = React.useState(true);
  const theme = getPrimaryTheme();

  const updateScrollState = React.useCallback(() => {
    const container = cardsRef.current;
    if (!container) return;

    const reachedEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 10;
    setIsScrolledToEnd(reachedEnd);
  }, []);

  const scrollNext = React.useCallback(() => {
    const container = cardsRef.current;
    if (!container) return;
    container.scrollBy({ left: container.clientWidth * 0.67, behavior: 'smooth' });
  }, []);

  React.useEffect(() => {
    if (!cards || cards.length <= 1) {
      setIsScrolledToEnd(true);
      return;
    }
    updateScrollState();
  }, [cards, updateScrollState]);

  if (!block) return null;

  if (!cards) {
    return renderMarkdown(content, 'fallback');
  }

  if (cards.length === 0) return null;

  return (
    <div>
      {renderMarkdown(block.before, 'before')}
      <div className="assistant-special-nav-wrapper">
        <div
          ref={cardsRef}
          className={`assistant-special-nav-cards ${isScrolledToEnd ? 'scrolled-to-end' : ''}`}
          onScroll={updateScrollState}
        >
          {cards.map((card, idx) => (
            <a
              key={`${card.title || 'card'}-${idx}`}
              href={card.url}
              target="_blank"
              rel="noopener noreferrer"
              className="assistant-special-nav-card"
            >
              <div
                className={`assistant-special-nav-thumb ${card.image ? 'has-image' : ''}`}
                style={!card.image ? { background: theme.navGradient } : undefined}
              >
                {card.image ? (
                  <img
                    src={card.image}
                    alt={card.title || 'Card'}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement?.classList.add('assistant-special-nav-thumb-placeholder');
                      e.currentTarget.parentElement.style.background = theme.navGradient;
                    }}
                  />
                ) : null}
                <PlaceholderIcon className="assistant-special-nav-thumb-icon" />
              </div>
              <div className="assistant-special-nav-content">
                <div className="assistant-special-nav-title">{card.title}</div>
                {card.description ? (
                  <div className="assistant-special-nav-description">{card.description}</div>
                ) : null}
              </div>
            </a>
          ))}
        </div>
        {cards.length > 1 && !isScrolledToEnd ? (
          <button
            type="button"
            className="assistant-special-nav-scroll-indicator"
            onClick={scrollNext}
            aria-label="Scroll navigation cards"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        ) : null}
      </div>
      {renderMarkdown(block.after, 'after')}
    </div>
  );
};

const ProductCards = ({ content }) => {
  const productBlock = parseBlock(content, ...START_END_MARKERS.product);
  const cardsBlock = parseBlock(content, ...START_END_MARKERS.productCards);
  const trainingCardsBlock = parseBlock(content, ...START_END_MARKERS.trainingCards);
  const listBlock = parseBlock(content, ...START_END_MARKERS.productList);
  const block = productBlock || cardsBlock || trainingCardsBlock || listBlock;

  if (!block) return null;
  const theme = getPrimaryTheme();

  try {
    const parsed = JSON.parse(block.raw);
    const source = Array.isArray(parsed) ? parsed : [parsed];
    const products = source.map(normalizeProduct).filter(Boolean);
    if (products.length === 0) return null;

    return (
      <div>
        {renderMarkdown(block.before, 'before')}
        <div className="assistant-special-products">
          {products.map((product, idx) => {
            const cardContent = (
              <>
                <div className={`assistant-special-product-image-wrap ${product.image ? 'has-image' : ''}`}>
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.title}
                      className="assistant-special-product-image"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement?.classList.add('image-error');
                      }}
                    />
                  ) : null}
                  <div
                    className="assistant-special-product-image-placeholder"
                    style={{ background: theme.imagePlaceholderGradient }}
                  >
                    <PlaceholderIcon
                      className="assistant-special-product-image-icon"
                      style={{ color: theme.primaryColor, opacity: 0.6 }}
                    />
                  </div>
                </div>
                <div className="assistant-special-product-body">
                  <div className="assistant-special-product-title">{product.title}</div>
                  {product.price ? (
                    <div className="assistant-special-product-price">{product.price}</div>
                  ) : null}
                  {product.description ? (
                    <div className="assistant-special-product-description">{product.description}</div>
                  ) : null}
                </div>
              </>
            );

            if (product.url) {
              return (
                <a
                  key={`${product.title}-${idx}`}
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="assistant-special-product"
                >
                  {cardContent}
                </a>
              );
            }

            return (
              <div key={`${product.title}-${idx}`} className="assistant-special-product">
                {cardContent}
              </div>
            );
          })}
        </div>
        {renderMarkdown(block.after, 'after')}
      </div>
    );
  } catch {
    return renderMarkdown(content, 'fallback');
  }
};

const Quiz = ({ content }) => {
  const block = parseBlock(content, ...START_END_MARKERS.quiz);
  if (!block) return null;

  try {
    const quiz = JSON.parse(block.raw);
    if (!quiz || !quiz.question || !Array.isArray(quiz.options)) return null;

    return (
      <div>
        {renderMarkdown(block.before, 'before')}
        <div className="assistant-special-quiz">
          <div className="assistant-special-quiz-question">{quiz.question}</div>
          <div className="assistant-special-quiz-options">
            {quiz.options.map((option, idx) => (
              <button
                type="button"
                key={`${option}-${idx}`}
                className="assistant-special-quiz-option"
              >
                {option}
              </button>
            ))}
          </div>
        </div>
        {renderMarkdown(block.after, 'after')}
      </div>
    );
  } catch {
    return renderMarkdown(content, 'fallback');
  }
};

const SpecialResponseRenderer = ({ content }) => {
  if (!containsMarkers(content)) return null;

  if (content.includes(START_END_MARKERS.navigation[0])) {
    return <NavigationCards content={content} />;
  }

  if (
    content.includes(START_END_MARKERS.product[0]) ||
    content.includes(START_END_MARKERS.productCards[0]) ||
    content.includes(START_END_MARKERS.trainingCards[0]) ||
    content.includes(START_END_MARKERS.productList[0])
  ) {
    return <ProductCards content={content} />;
  }

  if (content.includes(START_END_MARKERS.quiz[0])) {
    return <Quiz content={content} />;
  }

  return null;
};

export { containsMarkers as hasSpecialResponseMarkers };
export default SpecialResponseRenderer;
